import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { FileText, Download, Loader2, Search, File } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalDocuments = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchForms = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/personel-forms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data || []);
      }
    } catch (error) {
      console.error('Belgeler yüklenirken hata:', error);
      toast.error('Belgeler yüklenemedi');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const filteredForms = forms.filter(form => 
    form.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    form.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="portal-documents">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-violet-600" />
            Personel Talep Formları Arşivi
          </h1>
          <p className="text-gray-500 mt-1">İnsan Kaynakları tarafından yüklenen hazır evrak ve formları buradan indirebilirsiniz.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle>Hazır Belgeler</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Belge ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
            </div>
          ) : filteredForms.length === 0 ? (
            <div className="text-center py-12">
              <File className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-lg">Belge bulunamadı</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredForms.map((form) => (
                <div key={form.id} className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow relative group">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 pr-2">{form.title}</h3>
                  </div>
                  
                  {form.description && (
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">{form.description}</p>
                  )}
                  
                  {form.documents && form.documents.length > 0 && (
                    <div className="space-y-2 mt-4 pt-4 border-t border-gray-100">
                      {form.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                          <span className="truncate flex-1 pr-2" title={doc.name}>
                            {doc.name}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-100 shrink-0"
                            onClick={() => {
                              if (doc.url) {
                                window.open(doc.url, '_blank');
                              } else if (doc.data) {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-4 pt-2 text-xs text-gray-400">
                    <span>{new Date(form.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalDocuments;